"use client"

import { useEffect, useState } from "react"
import { useRemoteClientDeleteWithoutId, useRemoteClientPost } from "../../../../configs/http-client"
import { CatchMessageError } from "../../../../remote/catch-message-error"
import { useLocalStorage } from "../../../../remote/local-storage"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardHeader } from "../../../components/ui/card"
import { useToast } from "../../../hooks"
import { useSearchParams } from "react-router-dom"
import { convertParamsToJson, useCashfitParams } from "@/view/hooks/use-params"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog"
import { Edit, Trash2, Plus, Dumbbell, Search, FileText, ChevronLeft, ChevronRight, X } from "lucide-react"
import { Input } from "../../../components/ui/input"
import { Badge } from "../../../components/ui/badge"
import { format } from "date-fns"

interface Params {
  changeTab: (data: string) => void
}

interface PaginationInfo {
  totalElements: number
  totalPages: number
  size: number
  number: number
}

export function ListExerciseSheets({ changeTab }: Params) {
  const [sheets, setSheets] = useState<any[]>([])
  const [filteredSheets, setFilteredSheets] = useState<any[]>([])
  const { httpClientPost } = useRemoteClientPost()
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sheetToDelete, setSheetToDelete] = useState<string | null>(null)
  const [studentName, setStudentName] = useState("")
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize] = useState(20)
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
    totalElements: 0,
    totalPages: 0,
    size: 20,
    number: 0,
  })

  const { remoteClientDelete } = useRemoteClientDeleteWithoutId()
  const { showToast } = useToast()
  const { getUnitId } = useLocalStorage()
  const [params] = useSearchParams()
  const { setParams } = useCashfitParams(convertParamsToJson(params))

  const fetchSheets = async (page = 0, studentName?: string) => {
    setLoading(true)
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      })

      const requestBody: any = {
        idUnidade: getUnitId(),
      }

      if (studentName) {
        requestBody.nomeAluno = studentName
      }

      const response = await httpClientPost(`/fichas/filtro?${queryParams.toString()}`, { ...requestBody, status: 1 })

      const { content, numberOfElements: totalElements, totalPages, size, number } = response.data.data
      console.log("Fetched sheets:", content)

      setSheets(content || [])
      setFilteredSheets(content || [])
      setPaginationInfo({
        totalElements,
        totalPages,
        size,
        number,
      })
      setCurrentPage(number)
    } catch (error) {
      CatchMessageError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSheets()
  }, [])

  useEffect(() => {
    if (searchTerm) {
      const filtered = sheets.filter(
        (sheet) =>
          sheet.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sheet.aluno?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sheet.professor?.nome?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      setFilteredSheets(filtered)
    } else {
      setFilteredSheets(sheets)
    }
  }, [searchTerm, sheets])

  useEffect(() => {
    if (studentName.length >= 3) {
      setCurrentPage(0)
      fetchSheets(0, studentName)
    } else if (studentName.length === 0) {
      setCurrentPage(0)
      fetchSheets(0)
    }
  }, [studentName])

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < paginationInfo.totalPages) {
      fetchSheets(newPage, studentName)
    }
  }

  const handleEdit = (sheetId: string) => {
    setParams({ sheetId })
    changeTab("create-sheet")
  }

  const handleDelete = async () => {
    if (!sheetToDelete) return

    try {
      await remoteClientDelete(`/fichas/${sheetToDelete}?idUnidade=${getUnitId()}`)
      showToast("Ficha excluída com sucesso", "success")
      fetchSheets(currentPage, studentName)
    } catch (error) {
      CatchMessageError(error)
    } finally {
      setSheetToDelete(null)
      setDeleteDialogOpen(false)
    }
  }

  const confirmDelete = (sheetId: string) => {
    setSheetToDelete(sheetId)
    setDeleteDialogOpen(true)
  }

  const handleViewWorkouts = (sheetId: string, nameStudant: string) => {
    setParams({ sheetId: sheetId, nameStudent: nameStudant })
    
    changeTab("workout-list")
  }

  const handleCreateSheet = () => {
    setParams({ sheetId: "" })
    changeTab("create-sheet")
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "-"
    try {
      return format(new Date(dateString), "dd/MM/yyyy")
    } catch (e) {
      console.error("Error formatting date:", e)
      return dateString
    }
  }

  const handleClearStudentSearch = () => {
    setStudentName("")
    setSearchTerm("")
    setCurrentPage(0)
    fetchSheets(0)
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-4 mt-4">
          {/* Student Selector */}
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Nome do Aluno</label>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Digite pelo menos 3 caracteres..."
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                className="flex-1"
              />
              {studentName && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearStudentSearch}
                  className="px-3 py-2 text-sm whitespace-nowrap"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Search Input */}
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">Buscar</label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar fichas..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-acelerafit-green-400"></div>
          </div>
        ) : filteredSheets.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">
              {studentName ? `Nenhuma ficha encontrada para "${studentName}"` : "Não encontramos nenhuma ficha"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {studentName
                ? "Este aluno ainda não possui fichas de treino."
                : "Comece criando uma nova ficha de treino."}
            </p>
            <div className="mt-6">
              <Button
                onClick={handleCreateSheet}
                className="bg-acelerafit-green-400 text-white hover:bg-acelerafit-green-300"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nova Ficha
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Nome Ficha</TableHead>

                    <TableHead>Professor</TableHead>
                    <TableHead>Data de Início</TableHead>
                    <TableHead>Data de Término</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSheets.map((sheet) => (
                    <TableRow key={sheet.id}>
                      <TableCell className='font-bold' >{sheet.aluno?.nome || "-"}</TableCell>
                      <TableCell className="font-medium">{sheet.nome}</TableCell>
                      <TableCell>{sheet.professor?.nome || "-"}</TableCell>
                      <TableCell>{formatDate(sheet.dataInicio)}</TableCell>
                      <TableCell>{formatDate(sheet.dataFim)}</TableCell>
                      <TableCell>
                        {new Date(sheet.dataFim) >= new Date() ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Ativo</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                            Expirado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(sheet.id)}
                            className="h-8 w-8 p-0"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewWorkouts(sheet.id, sheet.aluno?.nome || "")}
                            className="h-8 w-8 p-0"
                            title="Ver Treinos"
                          >
                            <Dumbbell className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => confirmDelete(sheet.id)}
                            className="h-8 w-8 p-0"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                Mostrando {currentPage * pageSize + 1} a{" "}
                {Math.min((currentPage + 1) * pageSize, paginationInfo.totalElements)} de {paginationInfo.totalElements}{" "}
                resultados
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, Math.max(1, paginationInfo.totalPages)) }, (_, i) => {
                    let pageNumber
                    if (paginationInfo.totalPages <= 5) {
                      pageNumber = i
                    } else if (currentPage < 3) {
                      pageNumber = i
                    } else if (currentPage > paginationInfo.totalPages - 4) {
                      pageNumber = paginationInfo.totalPages - 5 + i
                    } else {
                      pageNumber = currentPage - 2 + i
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={currentPage === pageNumber ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                        className="w-8 h-8 p-0"
                        disabled={paginationInfo.totalPages <= 1}
                      >
                        {pageNumber + 1}
                      </Button>
                    )
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.totalPages - 1 || paginationInfo.totalPages <= 1}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação não poderá ser desfeita. Isso excluirá permanentemente a ficha de treino e todos os treinos
              associados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
